import { action, observable, makeObservable, runInAction } from "mobx";

import CouponsAPI, { CouponActivityLog } from "../api/CouponsAPI";
import { Coupon } from "../../../models/inventory/Coupon";
import { CouponFilters } from "../../../models/inventory/CouponsFilters";
import { ActivityLog } from "../../../models/Activity/ActivityLog";
import {
  APIBlobResponse,
  APIResponse,
  PaginatedAPIResponse,
} from "../../../models/NetworkResponse";

export type CouponActionConfirm = {
  title: string;
  message: string;
  confirmText: string;
  form: boolean;
  run: (input?: string) => Promise<unknown> | void;
};

export type CouponBulkJob = {
  jobId: number;
  queuedCount: number;
  skippedCount: number;
  progress: number;
  finished: boolean;
  success: boolean;
  summary: string;
  verb: string;
};

const flagLabel = (value: unknown) =>
  value === 1 ? "Yes" : value === 0 ? "No" : "—";

const toActivityLog = (row: CouponActivityLog): ActivityLog => {
  const before = row.changes?.before;
  const after = row.changes?.after;
  const lines = [
    `${row.action_label || row.action} coupon #${row.coupon_id} on order #${row.order_id}`,
    `Active: ${flagLabel(before?.active)} → ${flagLabel(after?.active)}`,
    `Expired: ${flagLabel(before?.expired)} → ${flagLabel(after?.expired)}`,
    `Expiry date: ${before?.expiry_date || "—"} → ${after?.expiry_date || "—"}`,
  ];
  if (row.platform) lines.push(`Platform: ${row.platform}`);
  return {
    id: row.id,
    activity: lines.join("\n"),
    created_by_name: row.admin_name || `Admin #${row.admin_id}`,
    created_at: row.created_date,
    order_id: row.order_id,
  };
};

export class CouponStore {
  coupons: Coupon[];
  couponsAPI: CouponsAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  actionConfirm: CouponActionConfirm | null;
  actionExpiryDate: string;
  actionError: string | null;
  isActionLoading: boolean;
  isBulkJobHeld: boolean;
  bulkJob: CouponBulkJob | null;
  isActivityLogsOpen: boolean;
  activityLogs: ActivityLog[];
  isActivityLogsLoading: boolean;
  activityLogsSubtitle: string | null;

  constructor() {
    this.coupons = [];
    this.couponsAPI = new CouponsAPI();

    this.limit = 0;
    this.total_count = 0;
    this.total_pages = 0;
    this.actionConfirm = null;
    this.actionExpiryDate = "";
    this.actionError = null;
    this.isActionLoading = false;
    this.isBulkJobHeld = false;
    this.bulkJob = null;
    this.isActivityLogsOpen = false;
    this.activityLogs = [];
    this.isActivityLogsLoading = false;
    this.activityLogsSubtitle = null;

    makeObservable(this, {
      coupons: observable,
      actionConfirm: observable.ref,
      actionExpiryDate: observable,
      actionError: observable,
      isActionLoading: observable,
      isBulkJobHeld: observable,
      bulkJob: observable.ref,
      isActivityLogsOpen: observable,
      activityLogs: observable,
      isActivityLogsLoading: observable,
      activityLogsSubtitle: observable,
      getCoupons: action,
      downloadReport: action,
      sendReminders: action,
      openActionConfirm: action,
      closeActionConfirm: action,
      setActionExpiryDate: action,
      setActionError: action,
      markActionSubmitting: action,
      finishActionSubmit: action,
      startBulkJob: action,
      patchBulkJob: action,
      dismissBulkJob: action,
      completeBulkJob: action,
      closeActivityLogs: action,
      loadActivityLogs: action,
    });
  }

  getCoupons = async (filters: CouponFilters) => {
    try {
      const response = (await this.couponsAPI.getCoupons(
        filters,
        false
      )) as PaginatedAPIResponse<Coupon>;
      this.limit = response.limit;
      this.total_count = response.total_count;
      this.total_pages = response.total_pages;
      this.coupons = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  downloadReport = async (filters: CouponFilters): Promise<APIBlobResponse> => {
    try {
      const response = (await this.couponsAPI.getCoupons(
        filters,
        true
      )) as APIBlobResponse;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  downloadShipmentsReport = async (
    filters: CouponFilters
  ): Promise<APIBlobResponse> => {
    try {
      const response = (await this.couponsAPI.getCouponsShipments(
        filters,
        true
      )) as APIBlobResponse;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  sendReminders = async (couponIds: number[]): Promise<APIResponse> => {
    try {
      const response = await this.couponsAPI.sendReminders(couponIds);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  reactivateCoupons = async (
    filters: CouponFilters,
    couponIds: number[]
  ): Promise<APIResponse> => {
    try {
      const response = await this.couponsAPI.reactivateCoupons(
        filters,
        couponIds
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  expireCoupons = async (
    filters: CouponFilters,
    couponIds: number[]
  ): Promise<APIResponse> => {
    try {
      const response = await this.couponsAPI.expireCoupons(filters, couponIds);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getCouponJob = async (jobId: number): Promise<APIResponse> => {
    try {
      return await this.couponsAPI.getCouponJob(jobId);
    } catch (error: any) {
      throw error;
    }
  };

  reactivateAllOnOrder = async (orderId: number): Promise<APIResponse> => {
    try {
      return await this.couponsAPI.reactivateAllOnOrder(orderId);
    } catch (error: any) {
      throw error;
    }
  };

  expireAllOnOrder = async (orderId: number): Promise<APIResponse> => {
    try {
      return await this.couponsAPI.expireAllOnOrder(orderId);
    } catch (error: any) {
      throw error;
    }
  };

  deleteCoupons = async (
    orderId: number,
    couponIds: number[]
  ): Promise<APIResponse> => {
    try {
      return await this.couponsAPI.deleteCoupons(orderId, couponIds);
    } catch (error: any) {
      throw error;
    }
  };

  deleteAllOnOrder = async (orderId: number): Promise<APIResponse> => {
    try {
      return await this.couponsAPI.deleteAllOnOrder(orderId);
    } catch (error: any) {
      throw error;
    }
  };

  updateExpiryDate = async (
    orderId: number,
    couponIds: number[],
    expiryDate: string
  ): Promise<APIResponse> => {
    try {
      return await this.couponsAPI.updateExpiryDate(
        orderId,
        couponIds,
        expiryDate
      );
    } catch (error: any) {
      throw error;
    }
  };

  openActionConfirm = (next: CouponActionConfirm) => {
    this.actionExpiryDate = "";
    this.actionError = null;
    this.isActionLoading = false;
    this.actionConfirm = next;
  };

  closeActionConfirm = () => {
    if (this.isActionLoading || this.isBulkJobHeld) return;
    this.actionConfirm = null;
    this.actionExpiryDate = "";
    this.actionError = null;
  };

  setActionExpiryDate = (value: string) => {
    this.actionExpiryDate = value;
    this.actionError = null;
  };

  setActionError = (message: string | null) => {
    this.actionError = message;
  };

  markActionSubmitting = () => {
    this.actionError = null;
    this.isActionLoading = true;
    if (!this.actionConfirm) return;
    const confirmText = this.actionConfirm.confirmText;
    this.actionConfirm = {
      ...this.actionConfirm,
      confirmText:
        confirmText === "Reactivate"
          ? "Reactivating"
          : confirmText === "Expire"
            ? "Expiring"
            : confirmText === "Delete"
              ? "Deleting"
              : confirmText === "Update"
                ? "Updating"
                : confirmText,
    };
  };

  finishActionSubmit = () => {
    this.isActionLoading = false;
    if (this.isBulkJobHeld) return;
    this.actionConfirm = null;
    this.actionExpiryDate = "";
  };

  startBulkJob = (
    verb: string,
    response: {
      result?: { jobId?: number; queuedCount?: number; skippedCount?: number };
    }
  ) => {
    const result = response.result || {};
    this.isBulkJobHeld = true;
    this.bulkJob = {
      jobId: Number(result.jobId),
      queuedCount: Number(result.queuedCount) || 0,
      skippedCount: Number(result.skippedCount) || 0,
      progress: 0,
      finished: false,
      success: false,
      summary: `Queued ${Number(result.queuedCount) || 0} coupons.`,
      verb,
    };
  };

  patchBulkJob = (jobId: number, patch: Partial<CouponBulkJob>) => {
    if (!this.bulkJob || this.bulkJob.jobId !== jobId) return;
    this.bulkJob = { ...this.bulkJob, ...patch };
  };

  dismissBulkJob = () => {
    this.isBulkJobHeld = false;
    this.bulkJob = null;
    this.actionConfirm = null;
    this.actionExpiryDate = "";
    this.actionError = null;
    this.isActionLoading = false;
  };

  completeBulkJob = () => {
    this.isBulkJobHeld = false;
    this.bulkJob = null;
    this.actionConfirm = null;
    this.actionExpiryDate = "";
  };

  closeActivityLogs = () => {
    this.isActivityLogsOpen = false;
  };

  loadActivityLogs = async () => {
    runInAction(() => {
      this.isActivityLogsOpen = true;
      this.isActivityLogsLoading = true;
      this.activityLogs = [];
      this.activityLogsSubtitle = null;
    });
    try {
      const loadPage = (pageNo: number) =>
        this.couponsAPI.getActivityLogs({
          page_no: pageNo,
          limit: 100,
        });
      const first = await loadPage(1);
      let rows = first?.list || [];
      const total = first?.total_count || rows.length;
      const pages = Math.min(first?.total_pages || 1, 10);
      for (let pageNo = 2; pageNo <= pages; pageNo += 1) {
        const next = await loadPage(pageNo);
        rows = rows.concat(next?.list || []);
      }
      runInAction(() => {
        this.activityLogs = rows.map(toActivityLog);
        this.activityLogsSubtitle =
          rows.length < total
            ? `Latest ${rows.length} of ${total}`
            : total
              ? `${total} log${total === 1 ? "" : "s"}`
              : null;
      });
    } catch (error: any) {
      runInAction(() => {
        this.activityLogs = [];
        this.activityLogsSubtitle = null;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.isActivityLogsLoading = false;
      });
    }
  };
}
